/**
 * User-facing copy for a sign-in Firebase rejected with a code the user can act on.
 * One mapping serves the email form, the SSO popup and the SSO redirect read back in
 * `authStore.initAuthListener`, so a disabled account reads the same on a phone.
 */
import { extractApiErrorMessage } from './errorHandling'
import { firebaseErrorCode } from './firebaseErrorCode'

export type SignInErrorMessageKey =
  | 'validation.invalidEmail'
  | 'auth.accountDisabled'
  | 'auth.invalidCredentials'
  | 'errors.tooManyAttempts'

const MESSAGE_KEY_BY_ERROR_CODE: Record<string, SignInErrorMessageKey> = {
  'auth/invalid-email': 'validation.invalidEmail',
  'auth/user-disabled': 'auth.accountDisabled',
  'auth/user-not-found': 'auth.invalidCredentials',
  'auth/wrong-password': 'auth.invalidCredentials',
  'auth/invalid-credential': 'auth.invalidCredentials',
  'auth/too-many-requests': 'errors.tooManyAttempts',
}

/** `undefined` for an unmapped code: the caller's own fallback applies. */
export function getSignInErrorMessageKey(error: unknown): SignInErrorMessageKey | undefined {
  const code = firebaseErrorCode(error)
  return code === undefined ? undefined : MESSAGE_KEY_BY_ERROR_CODE[code]
}

/** The mapped message where the code is known, else {@link extractApiErrorMessage}. */
export function getSignInErrorMessage(
  error: unknown,
  t: (key: SignInErrorMessageKey) => string,
  fallback: string,
): string {
  const key = getSignInErrorMessageKey(error)
  return key ? t(key) : extractApiErrorMessage(error, fallback)
}
